/**
 * Project colours.
 *
 * The colour lives in the Projects tab, not in localStorage as the old app
 * had it, so it follows the project rather than the browser.
 *
 * The palette is a constant-lightness ring in OKLCH at L = 0.623, which is
 * what the approved prototype's six colours turned out to be. Those six are
 * preserved exactly and come first; the rest fill in the ring at 20 degree
 * hue steps, with chroma capped at the sRGB gamut boundary.
 *
 * Every entry clears 3:1 contrast against both the light paper (#f9f9f7) and
 * the dark background (#17181a), which is the non-text threshold that matters
 * for a 3px border and a 9px dot. Measured range: 3.21-3.73 on light,
 * 4.52-5.25 on dark.
 *
 * The order matters. Entries are arranged so that consecutive ones sit far
 * apart in hue, because projects are assigned colours in sequence and the
 * ones that end up adjacent on screen are the ones that must not be confused.
 */
export const PALETTE = [
  "#1D9E75",
  "#D4537E",
  "#7F77DD",
  "#BA7517",
  "#D85A30",
  "#378ADD",
  "#9C6DCC",
  "#AA8000",
  "#0095BD",
  "#439D4D",
  "#0099A6",
  "#C35F9A",
  "#729520",
  "#5C82DE",
  "#009C90",
  "#B265B6",
  "#938B00",
  "#D05F58",
] as const;

export const FALLBACK_COLOR = "#8a8a92";

/**
 * The next unused colour, or a stable one derived from the name once the
 * palette is exhausted. Deterministic, so the same project list always
 * produces the same assignment.
 */
export function suggestColor(seed: string, taken: readonly string[] = []): string {
  const free = PALETTE.filter((c) => !taken.includes(c));
  if (free.length) return free[0]!;
  let hash = 0;
  for (const ch of seed) hash = (hash * 31 + ch.charCodeAt(0)) % 9973;
  return PALETTE[hash % PALETTE.length]!;
}

export function normaliseColor(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim() : FALLBACK_COLOR;
}
